import { useMemo, useState } from 'react';

type Selection = { annual: boolean; totals: Record<string, number> };
const emptySelection = (): Selection => ({ annual: false, totals: {} });

function readSelection(key: string | null): Selection {
  if (!key) return emptySelection();
  try {
    const value = JSON.parse(sessionStorage.getItem(key) || 'null');
    if (!value || typeof value.annual !== 'boolean' || !value.totals || typeof value.totals !== 'object') return emptySelection();
    return {
      annual: value.annual,
      totals: Object.fromEntries(Object.entries(value.totals).filter((entry): entry is [string, number] =>
        ['starter', 'team'].includes(entry[0]) && typeof entry[1] === 'number' && Number.isInteger(entry[1]) && entry[1] > 0)),
    };
  } catch {
    // Browsers can deny storage. Seat selection still works for this visit.
    return emptySelection();
  }
}

/** A tab's checkout draft survives the Stripe cancel link, scoped to its team. */
export function useBillingSeatSelection(teamId?: string) {
  const key = teamId ? `billing-seat-selection:${teamId}` : null;
  const persisted = useMemo(() => readSelection(key), [key]);
  const [drafts, setDrafts] = useState<Record<string, Selection>>({});
  const selection = drafts[key ?? 'visit'] ?? persisted;
  const update = (next: Selection) => {
    setDrafts(previous => ({ ...previous, [key ?? 'visit']: next }));
    if (key) {
      try { sessionStorage.setItem(key, JSON.stringify(next)); } catch { /* Optional persistence; retain the in-memory draft. */ }
    }
  };
  return {
    ...selection,
    setAnnual: (annual: boolean) => update({ ...selection, annual }),
    setTotal: (slug: string, total: number) => update({ ...selection, totals: { ...selection.totals, [slug]: total } }),
  };
}
